import React, { useEffect, useState } from 'react';
import ApproachVisualizer from './ApproachVisualizer';

const V1 = '#7F00FF', V2 = '#B163FF', GREEN = '#2ECC71', ORANGE = '#E8834C', RED = '#F04155';
const LEETCODE = '#FFA116';
const DIFFICULTY_COLOR = { Easy: GREEN, Medium: ORANGE, Hard: RED };

const LeetCodeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient id="dsaLcBadge" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FFC24B" />
        <stop offset="100%" stopColor="#FF9500" />
      </linearGradient>
    </defs>
    <rect width="24" height="24" rx="7" fill="url(#dsaLcBadge)" />
    <path d="M9 7L6.5 12L9 17" stroke="#20140A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 7L17.5 12L15 17" stroke="#20140A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LeetCodeLink = ({ leetcode }) => (
  <a
    href={leetcode.url}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 999,
      border: `1.5px solid ${LEETCODE}45`, background: `${LEETCODE}14`, color: LEETCODE,
      fontSize: 12.5, fontWeight: 800, textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
      fontFamily: "'Satoshi','DM Sans',sans-serif",
    }}
  >
    <LeetCodeIcon />
    LeetCode #{leetcode.number}
  </a>
);

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1400); });
  };
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '7px 12px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)' }}>
        <button onClick={copy} style={{
          fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
          border: `1px solid ${copied ? GREEN : 'var(--border)'}`,
          background: copied ? 'rgba(46,204,113,0.12)' : 'transparent',
          color: copied ? GREEN : 'var(--muted)', cursor: 'pointer',
        }}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <pre style={{ margin: 0, padding: '20px 22px', overflowX: 'auto', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 'clamp(14.5px, 0.7vw + 12px, 16.5px)', lineHeight: 1.8, color: '#E8E1FF' }}>
        <code>{code}</code>
      </pre>
    </div>
  );
};

const ApproachCard = ({ approach, index }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 18, padding: '30px 32px', background: 'var(--card-solid)' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
      <h4 style={{ fontFamily: "'Inter',sans-serif", fontSize: 'clamp(20px, 1vw + 15px, 22.5px)', fontWeight: 800, color: 'var(--text)', margin: 0 }}>
        {approach.name}
      </h4>
      <div style={{ display: 'flex', gap: 8 }}>
        <span style={pillStyle(V1)}>Time {approach.complexity.time}</span>
        <span style={pillStyle(V2)}>Space {approach.complexity.space}</span>
      </div>
    </div>

    <p style={{ fontSize: 'clamp(16px, 0.6vw + 14px, 18px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 22 }}>{approach.why}</p>

    <div style={{ fontSize: 'clamp(13px, 0.4vw + 11px, 14px)', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 13 }}>
      Step by step
    </div>
    <ol style={{ margin: 0, paddingLeft: 24, marginBottom: approach.visual ? 22 : 0, display: 'flex', flexDirection: 'column', gap: 11 }}>
      {approach.steps.map((s, i) => (
        <li key={i} style={{ fontSize: 'clamp(15.5px, 0.5vw + 13px, 17px)', lineHeight: 1.75, color: 'var(--text)' }}>{s}</li>
      ))}
    </ol>

    {approach.visual && <ApproachVisualizer visual={approach.visual} />}

    <CodeBlock code={approach.code} />

    {approach.interviewNote && (
      <div style={{
        marginTop: 18, padding: '15px 18px', borderRadius: 12, fontSize: 'clamp(14.5px, 0.4vw + 12px, 15.5px)', lineHeight: 1.7,
        background: `${V1}0c`, border: `1px solid ${V1}25`, color: 'var(--text-2)',
      }}>
        <b style={{ color: V2 }}>Worth mentioning: </b>{approach.interviewNote}
      </div>
    )}

    {approach.whenToUse && (
      <div style={{
        marginTop: 18, padding: '18px 20px', borderRadius: 12, fontSize: 'clamp(14.5px, 0.4vw + 12px, 15.5px)', lineHeight: 1.75,
        background: 'rgba(46,204,113,0.07)', border: '1px solid rgba(46,204,113,0.3)', color: 'var(--text-2)',
      }}>
        <div style={{ fontSize: 'clamp(12px, 0.3vw + 11px, 13px)', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: GREEN, marginBottom: 7 }}>
          Why &amp; when to use this approach
        </div>
        {approach.whenToUse}
      </div>
    )}
  </div>
);
const pillStyle = color => ({
  fontSize: 'clamp(12.5px, 0.3vw + 11px, 13.5px)', fontWeight: 800, padding: '6px 13px', borderRadius: 999,
  background: `${color}15`, color, whiteSpace: 'nowrap',
});

const DsaProblemModal = ({ problem, onClose }) => {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  if (!problem) return null;
  const diffColor = DIFFICULTY_COLOR[problem.difficulty] || V2;

  return (
    <div className="dsa-prob-overlay" onClick={onClose}>
      <div className="dsa-prob-modal" onClick={e => e.stopPropagation()}>
        <div className="dsa-prob-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
              <span style={{ fontSize: 'clamp(12.5px, 0.3vw + 11px, 13.5px)', fontWeight: 800, padding: '5px 13px', borderRadius: 999, background: `${diffColor}18`, color: diffColor }}>
                {problem.difficulty}
              </span>
            </div>
            <h2 style={{ fontFamily: "'Inter',sans-serif", fontSize: 'clamp(26px, 2vw + 17px, 33px)', fontWeight: 800, color: 'var(--text)', margin: 0 }}>
              {problem.title}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            {problem.leetcode && <LeetCodeLink leetcode={problem.leetcode} />}
            <button className="dsa-prob-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        <div className="dsa-prob-body">
          <p style={{ fontSize: 'clamp(16.5px, 0.6vw + 14px, 18.5px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 24 }}>{problem.statement}</p>

          <div style={{
            display: 'flex', flexDirection: 'column', gap: 9, background: 'var(--bg3)',
            border: '1px solid var(--border)', borderRadius: 14, padding: '20px 24px', marginBottom: 34,
            fontFamily: 'monospace', fontSize: 'clamp(15px, 0.4vw + 13px, 16.5px)',
          }}>
            <div><span style={{ color: 'var(--muted)', fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 700 }}>Input: </span><span style={{ color: 'var(--text)' }}>{problem.example.input}</span></div>
            <div><span style={{ color: 'var(--muted)', fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 700 }}>Output: </span><span style={{ color: GREEN, fontWeight: 700 }}>{problem.example.output}</span></div>
            {problem.example.note && (
              <div style={{ color: 'var(--muted)', fontFamily: "'Satoshi','DM Sans',sans-serif", fontSize: 'clamp(14px, 0.35vw + 12px, 15.5px)', marginTop: 7, lineHeight: 1.65 }}>{problem.example.note}</div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
            {problem.approaches.map((a, i) => <ApproachCard key={i} approach={a} index={i} />)}
          </div>
        </div>
      </div>

      <style>{`
        .dsa-prob-overlay {
          position: fixed; inset: 0; z-index: 9300;
          background: rgba(10,6,20,0.72); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; padding: 116px 24px 24px;
          animation: dsaProbFade 0.2s ease;
        }
        .dsa-prob-modal {
          width: min(1320px, 96vw); height: min(89vh, 960px);
          background: var(--card-solid); border: 1px solid var(--border); border-radius: 26px;
          display: flex; flex-direction: column; overflow: hidden;
          box-shadow: 0 30px 90px -12px rgba(0,0,0,0.5);
          animation: dsaProbPop 0.25s cubic-bezier(0.2,1,0.3,1);
        }
        .dsa-prob-header {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
          padding: 32px 38px 24px; border-bottom: 1px solid var(--border); flex-shrink: 0;
        }
        .dsa-prob-close {
          flex-shrink: 0; width: 40px; height: 40px; border-radius: 50%;
          border: 1px solid var(--border); background: var(--bg3); color: var(--muted);
          font-size: 15px; cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: background 0.2s;
        }
        .dsa-prob-close:hover { background: var(--bg2); color: var(--text); }
        .dsa-prob-body {
          flex: 1; overflow-y: auto; padding: 32px 38px 44px; min-height: 0;
          scroll-behavior: smooth; overscroll-behavior: contain; -webkit-overflow-scrolling: touch;
        }

        @keyframes dsaProbFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dsaProbPop { from { opacity: 0; transform: scale(0.96) translateY(12px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) {
          .dsa-prob-overlay, .dsa-prob-modal { animation: none !important; }
        }
        @media (max-width: 1180px) {
          .dsa-prob-modal { width: 94vw; }
        }
        @media (max-width: 720px) {
          .dsa-prob-overlay { padding: 84px 10px 10px; }
          .dsa-prob-modal { width: 98vw; height: 86vh; border-radius: 18px; }
          .dsa-prob-header { padding: 22px 20px 18px; }
          .dsa-prob-body { padding: 22px 20px 30px; }
        }
        @media (max-width: 420px) {
          .dsa-prob-overlay { padding: 78px 6px 6px; }
          .dsa-prob-modal { height: 90vh; border-radius: 14px; }
        }
      `}</style>
    </div>
  );
};

export default DsaProblemModal;
